import React, { forwardRef, useState, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { Check, X } from 'lucide-react';

/**
 * Standardized Button Component
 *
 * Follows consistent component API with:
 * - Variants: default, primary, secondary, outline, ghost, destructive, link
 * - Sizes: sm, md, lg, xl, icon
 * - States: default, hover, active, focus, disabled, loading, success, error
 * - Accessibility: WCAG 2.1 AAA compliant with 48px minimum touch target
 * - Visual feedback: Success/error states with auto-reset
 * - Micro-interactions: Hover transforms, ripple effects
 */

const buttonVariants = cva(
  // Base styles - using design system tokens
  "btn-base inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-all duration-200 relative overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-[hsl(var(--primary-hover))] hover:shadow-md hover:-translate-y-0.5",
        primary: "bg-primary text-primary-foreground shadow-md hover:bg-[hsl(var(--primary-hover))] hover:shadow-lg hover:-translate-y-0.5",
        secondary: "bg-secondary text-secondary-foreground hover:bg-[hsl(var(--secondary-hover))] hover:shadow-sm hover:-translate-y-0.5",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/50 hover:-translate-y-0.5",
        ghost: "hover:bg-accent hover:text-accent-foreground hover:shadow-sm",
        destructive: "bg-destructive text-destructive-foreground hover:bg-[hsl(var(--destructive-hover))] hover:shadow-md hover:-translate-y-0.5",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Mobile-first: 44px minimum touch target, desktop can be smaller
        sm: "h-11 px-3 text-xs min-h-[44px] md:h-9 md:min-h-[36px]", // 44px mobile, 36px desktop
        md: "h-11 px-4 py-2 text-sm min-h-[44px] md:h-10 md:min-h-[40px]", // 44px mobile, 40px desktop
        lg: "h-12 px-6 text-base min-h-[48px]", // 48px minimum touch target (always)
        xl: "h-14 px-8 text-lg min-h-[56px]", // Extra large (always)
        icon: "h-11 w-11 p-0 min-h-[44px] min-w-[44px] md:h-10 md:w-10 md:min-h-[40px] md:min-w-[40px]", // 44px mobile, 40px desktop
      },
      state: {
        default: "",
        loading: "pointer-events-none opacity-70 cursor-wait",
        disabled: "pointer-events-none opacity-50 cursor-not-allowed",
        success: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success-hover))]",
        error: "bg-destructive text-destructive-foreground hover:bg-[hsl(var(--destructive-hover))]",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        size: "sm",
        className: "h-9 px-3 text-xs",
      },
      {
        variant: "default",
        size: "lg",
        className: "h-12 px-6 text-base",
      },
      {
        variant: "primary",
        state: "loading",
        className: "opacity-70",
      },
      {
        variant: "outline",
        state: "error",
        className: "border-destructive text-destructive hover:bg-destructive/10",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "md",
      state: "default",
      fullWidth: false,
    },
  }
);

/**
 * Loading spinner component for buttons
 */
const LoadingSpinner = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
    xl: "h-6 w-6",
    icon: "h-4 w-4",
  };

  return (
    <svg
      className={cn("animate-spin", sizeClasses[size])}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

/**
 * Success icon for button feedback
 */
const SuccessIcon = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
    xl: "h-6 w-6",
    icon: "h-4 w-4",
  };

  return (
    <Check
      className={cn("animate-in zoom-in duration-200", sizeClasses[size])}
      aria-hidden="true"
    />
  );
};

/**
 * Error icon for button feedback
 */
const ErrorIcon = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "h-3 w-3",
    md: "h-4 w-4",
    lg: "h-5 w-5",
    xl: "h-6 w-6",
    icon: "h-4 w-4",
  };

  return (
    <X
      className={cn("animate-in zoom-in duration-200", sizeClasses[size])}
      aria-hidden="true"
    />
  );
};

/**
 * Ripple effect for button clicks
 */
const Ripple = ({ x, y, size }) => {
  return (
    <span
      className="absolute rounded-full bg-white dark:bg-card/30 pointer-events-none animate-ripple"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        transform: 'translate(-50%, -50%)',
      }}
    />
  );
};

/**
 * Standardized Button Component with Enhanced States
 */
export const Button = forwardRef(({
  className,
  variant = "default",
  size = "md",
  fullWidth = false,
  loading = false,
  success = false,
  error = false,
  disabled = false,
  children,
  leftIcon,
  rightIcon,
  type = "button",
  onClick,
  ...props
}, ref) => {
  const [internalState, setInternalState] = useState('default');
  const [ripples, setRipples] = useState([]);

  // Handle loading state
  const isLoading = loading || internalState === "loading";
  const isSuccess = success || internalState === "success";
  const isError = error || internalState === "error";
  const isDisabled = disabled || isLoading;

  // Auto-reset success/error states after 2 seconds
  useEffect(() => {
    if (success) {
      setInternalState('success');
      const timer = setTimeout(() => {
        setInternalState('default');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      setInternalState('error');
      const timer = setTimeout(() => {
        setInternalState('default');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Resolve final state
  let finalState = 'default';
  if (isLoading) finalState = 'loading';
  else if (isSuccess) finalState = 'success';
  else if (isError) finalState = 'error';
  else if (disabled) finalState = 'disabled';

  // Handle click with ripple effect
  const handleClick = (e) => {
    if (isDisabled) return;

    // Create ripple effect
    if (variant !== 'link' && variant !== 'ghost') {
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const newRipple = { x, y, size, id: Date.now() };
      setRipples(prev => [...prev, newRipple]);

      // Remove ripple after animation
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== newRipple.id));
      }, 600);
    }

    // Call original onClick
    if (onClick) {
      onClick(e);
    }
  };

  // Build component classes
  const buttonClasses = cn(
    buttonVariants({ variant, size, state: finalState, fullWidth }),
    className
  );

  // 2026-09-06, Phase 3b: the getToken dynamic styles (borderRadius /
  // transition duplicates of what rounded-md and the transition classes
  // already express) are removed with the useDesignSystem dependency —
  // same rationale as card.jsx: one authority, the CSS token system.

  // Render button with proper semantics
  return (
    <button
      type={type}
      className={buttonClasses}
      ref={ref}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      aria-describedby={isLoading ? "loading-description" : undefined}
      aria-live={isSuccess || isError ? "polite" : undefined}
      onClick={handleClick}
      {...props}
    >
      {/* Ripple effects */}
      {ripples.map(ripple => (
        <Ripple key={ripple.id} x={ripple.x} y={ripple.y} size={ripple.size} />
      ))}

      {/* Loading spinner */}
      {isLoading && <LoadingSpinner size={size} />}

      {/* Success icon */}
      {isSuccess && !isLoading && <SuccessIcon size={size} />}

      {/* Error icon */}
      {isError && !isLoading && <ErrorIcon size={size} />}

      {/* Left icon */}
      {leftIcon && !isLoading && !isSuccess && !isError && (
        <span className="flex-shrink-0" aria-hidden="true">
          {leftIcon}
        </span>
      )}

      {/* Button content */}
      <span className={cn(
        "inline-flex items-center justify-center gap-1 transition-opacity duration-200",
        (isLoading || isSuccess || isError) && "opacity-0"
      )}>
        {children}
      </span>

      {/* Right icon */}
      {rightIcon && !isLoading && !isSuccess && !isError && (
        <span className="flex-shrink-0" aria-hidden="true">
          {rightIcon}
        </span>
      )}

      {/* Screen reader feedback */}
      {isLoading && (
        <span id="loading-description" className="sr-only">
          Loading, please wait
        </span>
      )}
      {isSuccess && (
        <span className="sr-only">
          Success
        </span>
      )}
      {isError && (
        <span className="sr-only">
          Error occurred
        </span>
      )}
    </button>
  );
});

Button.displayName = "Button";

/**
 * Button Group for related actions
 */
export const ButtonGroup = ({
  children,
  orientation = "horizontal",
  spacing = "sm",
  className,
  ...props
}) => {
  const orientationClasses = {
    horizontal: "flex-row",
    vertical: "flex-col",
  };

  return (
    <div
      className={cn(
        "flex gap-2",
        orientationClasses[orientation],
        className
      )}
      role="group"
      {...props}
    >
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          // Add proper ARIA attributes for button group
          return React.cloneElement(child, {
            'aria-label': child.props['aria-label'] || `Action ${index + 1}`,
            'aria-describedby': child.props['aria-describedby'] || undefined,
          });
        }
        return child;
      })}
    </div>
  );
};

/**
 * Icon Button variant
 */
export const IconButton = forwardRef(({
  icon,
  label,
  tooltip,
  variant = "ghost",
  className,
  ...props
}, ref) => {
  return (
    <Button
      ref={ref}
      size="icon"
      variant={variant}
      className={cn("p-0", className)}
      aria-label={label}
      title={tooltip || label}
      {...props}
    >
      <span className="flex-shrink-0" aria-hidden="true">
        {icon}
      </span>
    </Button>
  );
});

IconButton.displayName = "IconButton";

/**
 * Floating Action Button
 */
export const FloatingActionButton = forwardRef(({
  icon,
  label,
  position = "bottom-right",
  className,
  ...props
}, ref) => {
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'top-right': 'fixed top-6 right-6',
    'top-left': 'fixed top-6 left-6',
  };

  return (
    <Button
      ref={ref}
      size="lg"
      variant="primary"
      className={cn(
        positionClasses[position],
        "rounded-full shadow-lg hover:shadow-xl transition-shadow duration-200 min-h-[56px] min-w-[56px]",
        className
      )}
      aria-label={label}
      {...props}
    >
      <span className="flex-shrink-0" aria-hidden="true">
        {icon}
      </span>
    </Button>
  );
});

FloatingActionButton.displayName = "FloatingActionButton";

// Export variants for testing and customization
export { buttonVariants };

export default Button;
